import produce from "immer";
import { compact, each, flatMap, head, map, size, uniqBy } from "lodash";
import { Period } from "@linkfluence/models/date";
import { Card } from "../cards";
import { setCardPeriod } from "../cards/editor";
import { cardWithoutPeriod, isCardWithRequest } from "../cards/is";
import { CardPeriod } from "../cards/model";
import { getDateFromClause } from "../clause/getter";
import { AIFragment, SearchFragment } from "../search";
import { InsightPage, Row } from "./model";
import { isCardRow, isTitleRow } from "./row";

export function setInsightPagePeriod(page: InsightPage, period: CardPeriod) {
  return produce(page, (draft) => {
    each(draft.rows, (row) => {
      if (isCardRow(row)) {
        row.cards = setCardsPeriod(row.cards, period);
      }
    });
  });
}

export function getPeriodFromInsightPage(page: InsightPage) {
  const periods = getUniqPeriods(getAllPeriodsFromInsightPage(page));
  if (hasMultiplePeriods(periods)) {
    return null;
  }
  return head(periods);
}

function hasMultiplePeriods(periods: Period[]) {
  return size(periods) !== 1;
}

function getUniqPeriods(periods: Period[]) {
  return uniqBy(periods, ({ from, to }) => from + "::" + to);
}

function getAllPeriodsFromInsightPage(page: InsightPage) {
  return compact(flatMap(map(page?.rows, getPeriodFromRow)));
}

function getPeriodFromRow(row: Row) {
  if (isTitleRow(row)) {
    return [];
  }
  return isCardRow(row) && map(row.cards, getPeriodFromCard);
}

function getPeriodFromCard(card: Card) {
  if (!card || cardWithoutPeriod(card) || !isCardWithRequest(card)) {
    return null;
  }
  return getPeriodFromFragment(card.fragments?.[0]);
}

function getPeriodFromFragment(fragment: SearchFragment | AIFragment) {
  if (!fragment) {
    return null;
  }
  return getDateFromClause(fragment.request.query);
}

function setCardsPeriod<T extends Card[]>(cards: T, period: CardPeriod) {
  return produce(cards, (draft) => {
    each(draft, (card, idx) => {
      if (card && !cardWithoutPeriod(card)) {
        draft[idx] = setCardPeriod(card, period);
      }
    });
  });
}

export const enablePeriodSelector = (page: InsightPage, enable: boolean) => {
  return produce(page, (draft) => {
    draft.periodSelector = enable;
  });
};
